/**
 * Fallback helpers until engine-type-specific config APIs land.
 * Form-level defaults / conditions / tooltips come from valuation/engine-valuation/form/.
 * Output valuation values come only from the API response — no mock numbers.
 */

export const ENGINE_CONDITION_OPTIONS = [
  { value: 'new', label: 'New Engine' },
  { value: 'used', label: 'Used Engine' },
];

export const DEFAULT_SHOP_VISIT_OPTIONS = [
  { value: 'sv1', label: 'First Run (SV1)', svNumber: 1 },
  { value: 'sv2', label: 'Mature Run (SV2)', svNumber: 2 },
  { value: 'sv3', label: 'Mature Run (SV3)', svNumber: 3 },
];

export const DEFAULT_QEC_OPTIONS = [
  { value: 'basic', label: 'Basic QEC' },
  { value: 'full', label: 'Full QEC' },
  { value: 'bare', label: 'Bare Engine' },
];

/** Strip "Optional" / "valuation team only" phrasing from API or local tips */
export const sanitizeFieldTip = (tip) => {
  if (tip == null || tip === '') return '';
  if (typeof tip !== 'string') return tip;
  return tip
    .replace(/\(\s*optional\s*[-–—,]?\s*valuation[-\s]?team\s+only\s*\)\.?/gi, '')
    .replace(/\bvaluation[-\s]?team\s+only\.?\s*/gi, '')
    .replace(/\boptional\.?\s*/gi, '')
    .replace(/\(\s*optional\s*\)\.?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s.\-–—]+|[\s.\-–—]+$/g, '')
    .trim();
};

export const FIELD_TOOLTIPS = {
  serial_number: 'Engine serial number.',
  engine_type: 'Drives base value, depreciation, factors, QEC options and shop-visit costs.',
  date_of_manufacture: 'Used to derive the maintenance maturity year. Half-life base value does not change with DOM.',
  qec_type: 'QEC configuration for the selected engine type. A bare engine has a zero value.',
  shop_visit_status: 'SV1 is first run. SV2 and SV3 are mature runs and select the maintenance cost.',
  condition: 'New or Used. New is not permitted when shop-visit status is SV2 or later. Used is allowed on SV1.',
  qec_discount: 'Percent reduction applied to the QEC value.',
  escalation_percentage: 'Annual percent applied to future base value and future base lease rate.',
  inflation_rate: 'Annual percent applied to future base value and future base lease rate.',
  projection_to: 'End date of the future projection.',
  base_value_date: 'Valuation reference date.',
  value_as_of: 'Valuation reference date.',
  premium_for_new: 'Applies the new-engine premium when condition is New.',
  premium_pct: 'Percent applied to the premium base cost.',
  premium_percentage: 'Percent applied to the premium base cost.',
};

/** Normalize shop-visit value (sv1 / 1 / {value}) to a number for condition_rules */
export const shopVisitToNumber = (shopVisitStatus) => {
  if (shopVisitStatus == null || shopVisitStatus === '') return null;
  if (typeof shopVisitStatus === 'number') return shopVisitStatus;
  if (typeof shopVisitStatus === 'object') {
    if (shopVisitStatus.svNumber != null) return Number(shopVisitStatus.svNumber);
    shopVisitStatus = shopVisitStatus.value;
  }
  const raw = String(shopVisitStatus).toLowerCase();
  const match = raw.match(/(\d+)/);
  return match ? Number(match[1]) : null;
};

/** Map field_guidance keys from form API onto local field tip keys */
export const mapFieldGuidance = (fieldGuidance = {}) => {
  const merged = {
    ...FIELD_TOOLTIPS,
    ...fieldGuidance,
    escalation_percentage: fieldGuidance.inflation_rate || fieldGuidance.escalation_percentage || FIELD_TOOLTIPS.escalation_percentage,
    premium_pct: fieldGuidance.premium_percentage || fieldGuidance.premium_pct || FIELD_TOOLTIPS.premium_pct,
    base_value_date: fieldGuidance.value_as_of || fieldGuidance.base_value_date || FIELD_TOOLTIPS.base_value_date,
  };
  return Object.keys(merged).reduce((acc, key) => {
    acc[key] = sanitizeFieldTip(merged[key]);
    return acc;
  }, {});
};

export const getMockEngineTypeConfig = (engineType) => {
  const name = (engineType && (engineType.name || engineType.label) || '').toLowerCase();
  let qecOptions = [...DEFAULT_QEC_OPTIONS];
  let inflation_rate = 2;
  let premium_pct = 5;

  if (name.includes('trent')) {
    inflation_rate = 2;
    premium_pct = 3;
  } else if (name.includes('pw')) {
    inflation_rate = 2.5;
    premium_pct = 5;
  }

  return {
    qecOptions,
    shopVisitOptions: DEFAULT_SHOP_VISIT_OPTIONS,
    defaults: {
      inflation_rate,
      premium_pct,
      qec_type: qecOptions[0],
      shop_visit_status: 'sv1',
      condition: 'new',
      premium_for_new: 'yes',
      qec_discount: 0,
    },
  };
};
